import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Trash2 } from "lucide-react";
import { localeOf } from "../../i18n";

export function AdminPoints() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.get("/point-config").then(({ data }) => setCfg(data)); }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/point-config", {
        default_attendance_points: Number(cfg.default_attendance_points) || 0,
        default_mission_points: Number(cfg.default_mission_points) || 0,
        image_retention_days: Number(cfg.image_retention_days) || 0,
      });
      setCfg(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!cfg) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div className="max-w-xl space-y-5" data-testid="admin-points-page">
      <h1 className="font-display text-3xl font-extrabold">{t("admin.globalConfig")}</h1>
      <div className="vy-card space-y-4 p-5">
        {[
          { key: "default_attendance_points", label: t("admin.attendancePoints"), testId: "config-attendance-points" },
          { key: "default_mission_points", label: t("admin.missionPoints"), testId: "config-mission-points" },
          { key: "image_retention_days", label: t("admin.retention"), testId: "config-retention-days" },
        ].map((f) => (
          <div key={f.key}>
            <label className="text-sm font-semibold">{f.label}</label>
            <Input data-testid={f.testId} type="number" className="mt-1.5 h-12" value={cfg[f.key] ?? 0}
              onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })} />
          </div>
        ))}
        <Button data-testid="config-save-btn" onClick={save} className="h-12 w-full rounded-full font-bold">
          {t("common.save")}
        </Button>
      </div>
      <RetentionPanel />
    </div>
  );
}

function RetentionPanel() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/maintenance/retention").then(({ data }) => setInfo(data));
  useEffect(() => { load().catch(() => {}); }, []);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/maintenance/purge-images");
      if (data.errors > 0) toast.error(t("retention.purgeErrors", { n: data.errors }));
      else toast.success(t("retention.purged", { n: data.purged }));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!info) return null;
  return (
    <div className="vy-card space-y-4 p-5" data-testid="retention-panel">
      <div>
        <h2 className="font-display text-lg font-bold">{t("retention.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("retention.lead", { days: info.retention_days })}</p>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl bg-muted p-3">
          <p className="font-display text-xl font-bold" data-testid="retention-stored">{info.images_stored}</p>
          <p className="text-[11px] font-semibold text-muted-foreground">{t("retention.stored")}</p>
        </div>
        <div className="rounded-xl bg-muted p-3">
          <p className="font-display text-xl font-bold" data-testid="retention-expired">{info.expired_pending}</p>
          <p className="text-[11px] font-semibold text-muted-foreground">{t("retention.expired")}</p>
        </div>
        <div className="rounded-xl bg-muted p-3">
          <p className="font-display text-xl font-bold" data-testid="retention-purged">{info.purged_total}</p>
          <p className="text-[11px] font-semibold text-muted-foreground">{t("retention.purgedTotal")}</p>
        </div>
      </div>
      <Button variant="outline" data-testid="retention-purge-btn" onClick={purge} disabled={busy}
        className="h-12 w-full rounded-full font-bold">
        <Trash2 className="mr-2 h-4 w-4" />{busy ? t("common.loading") : t("retention.purgeNow")}
      </Button>
      {info.recent?.length > 0 && (
        <ul className="divide-y divide-border text-xs text-muted-foreground">
          {info.recent.slice(0, 5).map((r) => (
            <li key={r.id} className="py-2" data-testid={`purge-log-${r.id}`}>
              {fmtDate(r.deleted_at, locale)} · {r.actor} · {r.retention_days} {t("retention.days")}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">{t("retention.dailyJob")}</p>
    </div>
  );
}

export function StaffReports() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [ledger, setLedger] = useState([]);
  const locale = localeOf(i18n.language);

  useEffect(() => {
    api.get("/reports/participation").then(({ data }) => setRows(data));
    api.get("/ledger").then(({ data }) => setLedger(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="staff-reports-page">
      <h1 className="font-display text-3xl font-extrabold">{t("nav.reports")}</h1>

      <section className="vy-card overflow-x-auto p-5">
        <h2 className="font-display text-lg font-bold">{t("profile.title")}</h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("admin.name")}</th>
              <th className="py-2">{t("admin.team")}</th>
              <th className="py-2">{t("home.attended")}</th>
              <th className="py-2">{t("profile.noShows")}</th>
              <th className="py-2">{t("home.missions")}</th>
              <th className="py-2">{t("home.score")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-border" data-testid={`report-row-${r.user_id}`}>
                <td className="py-3 font-semibold">{r.name}</td>
                <td className="py-3 text-muted-foreground">{r.team_name || "—"}</td>
                <td className="py-3">{r.attended}</td>
                <td className="py-3">{r.no_shows}</td>
                <td className="py-3">{r.missions_approved}</td>
                <td className="py-3 font-bold">{r.score}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="vy-card overflow-x-auto p-5" data-testid="ledger-section">
        <h2 className="font-display text-lg font-bold">{t("nav.ledger")}</h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("admin.name")}</th>
              <th className="py-2">{t("common.reason")}</th>
              <th className="py-2">{t("common.points")}</th>
              <th className="py-2">{t("profile.history")}</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`ledger-row-${l.id}`}>
                <td className="py-3 font-semibold">{l.user_name}</td>
                <td className="py-3 text-muted-foreground">{l.reason} <span className="text-xs">({l.source})</span></td>
                <td className={`py-3 font-bold ${l.points >= 0 ? "" : "text-destructive"}`}>
                  {l.points >= 0 ? "+" : ""}{l.points}
                </td>
                <td className="py-3 text-xs text-muted-foreground">{fmtDate(l.created_at, locale)} · {l.actor_name}</td>
              </tr>
            ))}
            {ledger.length === 0 && <tr><td colSpan={4} className="py-4 text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
